import React from 'react';
import styled from 'styled-components';
import { MobileCardContainer } from '../styles/DashboardMobileStyles';
import { colors, typography, spacing } from '../styles/designTokens';

// 모바일 통계 카드 Props 인터페이스
interface StatsCardMobileProps {
  title: string;
  value: number;
  unit?: string; // 단위 추가 (예: '명', '원')
  metaItems?: Array<{
    label: string;
    value: number;
    color?: string;
  }>;
  change?: {
    text: string;
    color: string;
    icon: string;
  };
  formatNumber: (num: number) => string;
  clickable?: boolean;
  onClick?: () => void;
  isActive?: boolean;
}

// 모바일 카드 제목 스타일
const MobileCardTitle = styled.h3`
  margin: 0 0 ${spacing[2]} 0;
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  color: ${colors.semantic.text.tertiary};
  line-height: ${typography.lineHeight.tight};
`;

// 모바일 카드 값 스타일
const MobileCardValue = styled.div<{ $hasMeta?: boolean }>`
  font-size: ${typography.fontSize.xl};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.semantic.text.primary};
  margin-bottom: ${({ $hasMeta }) => ($hasMeta ? spacing[0] : spacing[1])};
  line-height: ${typography.lineHeight.tight};
`;

const MobileCardMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 8px;
  margin: 0 0 ${spacing[1]} 0;
`;

const MobileMetaChip = styled.div<{ $color?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 999px;
  border: 1px solid ${colors.semantic.border.primary};
  background: ${colors.secondary[50]};

  .label {
    color: ${colors.secondary[600]};
    font-size: 11px;
    font-weight: 800;
  }
  .value {
    color: ${({ $color }) => $color || colors.secondary[800]};
    font-size: 11px;
    font-weight: 900;
  }
`;

// 모바일 카드 변화 지표 스타일
const MobileCardChange = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: ${props => props.color};
  font-weight: 500;
  
  .icon {
    font-size: 10px;
  }
`;

// 모바일 통계 카드 컴포넌트
const StatsCardMobile: React.FC<StatsCardMobileProps> = ({
  title,
  value,
  unit,
  metaItems,
  change,
  formatNumber,
  clickable = false,
  onClick,
  isActive = false
}) => {
  return (
    <MobileCardContainer
      $clickable={clickable}
      onClick={onClick}
      onKeyDown={
        clickable && onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-pressed={clickable ? isActive : undefined}
      aria-label={
        clickable ? `${title}, 아래 표 영역 표시를 전환합니다` : undefined
      }
      $isActive={isActive}
    >
      <MobileCardTitle>{title}</MobileCardTitle>
      <MobileCardValue $hasMeta={!!metaItems && metaItems.length > 0}>
        {formatNumber(value)}{unit && <span style={{ fontSize: '14px', marginLeft: '2px' }}>{unit}</span>}
      </MobileCardValue>

      {metaItems && metaItems.length > 0 && (
        <MobileCardMeta>
          {metaItems.map(item => (
            <MobileMetaChip
              key={item.label}
              $color={item.color}
              title={`${item.label}: ${item.value}`}
            >
              <span className="label">{item.label}</span>
              <span className="value">{formatNumber(item.value)}</span>
            </MobileMetaChip>
          ))}
        </MobileCardMeta>
      )}
      {change && (
        <MobileCardChange color={change.color}>
          <span className="icon">{change.icon}</span>
          <span>{change.text}</span>
        </MobileCardChange>
      )}
    </MobileCardContainer>
  );
};

export default StatsCardMobile; 